// MetaApi Service for TidalWave Pro
// Handles real trading connections to MetaTrader accounts via REST API
// Includes: trade tracking, SL/TP, graceful shutdown, lifecycle logging, force-close

// Fallback token (for development - should use env var in production)
const FALLBACK_TOKEN=process.env.REACT_APP_FALLBACK_TOKEN;  

// Get token from environment variable or use fallback
const getToken = () => {
  const token = process.env.REACT_APP_METAAPI_TOKEN;
  console.log('Available env vars:', Object.keys(process.env).filter(k => k.includes('REACT')));
  console.log('Token present:', !!token);
  
  // Return env token if available, otherwise use fallback
  if (token) return token;
  
  // Use fallback token
  console.log('Using fallback token');
  return FALLBACK_TOKEN;
};

const API_URL = 'https://mt-api.metaapi.cloud';

// ═══════════════════════════════════════════════════════════════
// RATE LIMITING & TRADE QUEUE MANAGEMENT
// ═══════════════════════════════════════════════════════════════

// Rate limiter using token bucket algorithm
class RateLimiter {
  constructor(maxTokens = 5000, windowMs = 10000) {
    this.maxTokens = maxTokens;
    this.windowMs = windowMs;
    this.tokens = maxTokens;
    this.lastRefill = Date.now();
    this.requestQueue = [];
    this.processing = false;
    this.minRequestInterval = 200; // Minimum 200ms between requests
    this.lastRequestTime = 0;
  }

  // Refill tokens based on time elapsed
  refill() {
    const now = Date.now();
    const elapsed = now - this.lastRefill;
    const tokensToAdd = (elapsed / this.windowMs) * this.maxTokens;
    this.tokens = Math.min(this.maxTokens, this.tokens + tokensToAdd);
    this.lastRefill = now;
  }

  // Check if we can make a request
  async acquire(tokensNeeded = 1) {
    this.refill();
    
    // Wait for minimum interval
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.minRequestInterval) {
      await new Promise(resolve => setTimeout(resolve, this.minRequestInterval - timeSinceLastRequest));
    }
    
    // Wait for tokens
    while (this.tokens < tokensNeeded) {
      const waitTime = ((tokensNeeded - this.tokens) / this.maxTokens) * this.windowMs;
      console.log(`[RateLimit] Waiting ${Math.ceil(waitTime)}ms for tokens...`);
      await new Promise(resolve => setTimeout(resolve, Math.min(waitTime, 1000)));
      this.refill();
    }
    
    this.tokens -= tokensNeeded;
    this.lastRequestTime = Date.now();
    return true;
  }

  // Get current token status
  getStatus() {
    this.refill();
    return {
      tokens: Math.round(this.tokens),
      available: this.tokens >= 1,
      usagePercent: ((this.maxTokens - this.tokens) / this.maxTokens * 100).toFixed(1)
    };
  }
}

// Trade queue for managing pending trades
class TradeQueue {
  constructor() {
    this.queue = [];
    this.processing = false;
    this.maxConcurrentTrades = 1;
    this.activeTrades = new Map(); // symbol -> side
    this.symbolLocks = new Map(); // Prevent opposite direction trades
  }

  // Add trade to queue
  enqueue(trade) {
    return new Promise((resolve, reject) => {
      this.queue.push({ trade, resolve, reject, addedAt: Date.now() });
      this.processQueue();
    });
  }

  // Check if we can open trade on symbol (no opposite direction)
  canOpenTrade(symbol, side) {
    const lockedSide = this.symbolLocks.get(symbol);
    if (lockedSide && lockedSide !== side) {
      return { allowed: false, reason: `Opposite trade already in progress on ${symbol}` };
    }
    return { allowed: true };
  }

  // Lock symbol for a trade
  lockSymbol(symbol, side) {
    this.symbolLocks.set(symbol, side);
  }

  // Unlock symbol
  unlockSymbol(symbol) {
    this.symbolLocks.delete(symbol);
  }

  // Process queue
  async processQueue() {
    if (this.processing || this.queue.length === 0) return;
    
    this.processing = true;
    
    while (this.queue.length > 0) {
      const { trade, resolve, reject, addedAt } = this.queue[0];
      
      // Check if trade is stale (older than 60 seconds)
      if (Date.now() - addedAt > 60000) {
        this.queue.shift();
        reject(new Error('Trade request timed out'));
        continue;
      }
      
      // Check concurrent trade limit
      if (this.activeTrades.size >= this.maxConcurrentTrades) {
        await new Promise(r => setTimeout(r, 500));
        continue;
      }
      
      // Check opposite direction lock
      const canTrade = this.canOpenTrade(trade.symbol, trade.side);
      if (!canTrade.allowed) {
        await new Promise(r => setTimeout(r, 1000));
        continue;
      }
      
      // Remove from queue and process
      this.queue.shift();
      
      try {
        this.lockSymbol(trade.symbol, trade.side);
        this.activeTrades.set(trade.id || trade.symbol, trade.side);
        
        const result = await trade.execute();
        
        resolve(result);
      } catch (error) {
        reject(error);
      } finally {
        this.activeTrades.delete(trade.id || trade.symbol);
        this.unlockSymbol(trade.symbol);
      }
    }
    
    this.processing = false;
  }

  // Get queue status
  getStatus() {
    return {
      queueLength: this.queue.length,
      activeTrades: this.activeTrades.size,
      locks: Array.from(this.symbolLocks.entries())
    };
  }
}

// Retry handler with exponential backoff
class RetryHandler {
  constructor(maxRetries = 3, baseDelay = 1000) {
    this.maxRetries = maxRetries;
    this.baseDelay = baseDelay;
  }

  async execute(fn, context = '') {
    let lastError;
    
    for (let attempt = 1; attempt <= this.maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error) {
        lastError = error;
        
        // Don't retry for certain errors
        if (error.message?.includes('Position not found')) {
          throw error;
        }
        if (error.message?.includes('Authentication')) {
          throw error;
        }
        
        const delay = this.baseDelay * Math.pow(2, attempt - 1);
        console.log(`[Retry] ${context} - Attempt ${attempt}/${this.maxRetries} failed: ${error.message}. Retrying in ${delay}ms...`);
        
        if (attempt < this.maxRetries) {
          await new Promise(r => setTimeout(r, delay));
        }
      }
    }
    
    throw lastError;
  }
}

// Trade lifecycle event logger
class TradeLogger {
  constructor() {
    this.events = [];
    this.maxEvents = 1000;
  }

  log(event) {
    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, ...event };
    this.events.push(logEntry);
    
    // Keep only recent events
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }
    
    // Console output with formatting
    console.log(`[TRADE ${event.type?.toUpperCase() || 'LOG'}] ${timestamp}:`, event);
  }

  getEvents() {
    return [...this.events];
  }

  clear() {
    this.events = [];
  }

  getEventsByTradeId(tradeId) {
    return this.events.filter(e => e.tradeId === tradeId);
  }
}

// Cache for real symbols
const foundRealSymbols = {
  BTCUSD: null,
  EURUSD: null,
  GBPUSD: null,
  USDJPY: null,
  XAUUSD: null,
};

class MetaApiService {
  constructor() {
    this.token = '';
    this.accountId = null;
    this.isConnected = false;
    this.isConnecting = false;
    
    // Rate limiting system
    this.rateLimiter = new RateLimiter(5000, 10000); // 5000 credits per 10 seconds
    this.tradeQueue = new TradeQueue();
    this.retryHandler = new RetryHandler(3, 1000);
    
    // Trade tracking system
    this._openPositions = new Map(); // Internal tracking: positionId -> position data
    this._positionOrder = []; // Track order of position opening for FIFO closing
    
    // Trade logger
    this.tradeLogger = new TradeLogger();
    
    // Callbacks for real-time updates
    this._onPositionUpdate = null;
    this._onPositionClose = null;
    this._onTradeError = null;
    this._onReconnect = null;
    
    // Configuration
    this.closingMethod = 'FIFO'; // FIFO (First In First Out) or LIFO (Last In First Out)
    this.maxOpenTrades = 3; // Maximum concurrent trades
    
    // Sync state
    this._lastSyncTime = null;
    this._syncInterval = null;
    
    // Trading state
    this._isTradingEnabled = false;
    this._lastTradeTime = 0;
    this._minTradeInterval = 3000; // 3 seconds minimum between trades
  }

  // Enable/disable trading
  setTradingEnabled(enabled) {
    this._isTradingEnabled = enabled;
    this.tradeLogger.log({
      type: 'TRADING_STATE_CHANGE',
      enabled
    });
  }

  // Set max concurrent trades
  setMaxConcurrentTrades(max) {
    this.maxOpenTrades = Math.max(1, Math.min(10, max));
    this.tradeQueue.maxConcurrentTrades = this.maxOpenTrades;
    this.tradeLogger.log({
      type: 'CONFIG_CHANGE',
      maxConcurrentTrades: this.maxOpenTrades
    });
  }

  // Get rate limiter status
  getRateLimitStatus() {
    return this.rateLimiter.getStatus();
  }

  // Get trade queue status
  getQueueStatus() {
    return this.tradeQueue.getStatus();
  }

  // Register callbacks for events
  onPositionUpdate(callback) {
    this._onPositionUpdate = callback;
  }

  onPositionClose(callback) {
    this._onPositionClose = callback;
  }

  onTradeError(callback) {
    this._onTradeError = callback;
  }

  onReconnect(callback) {
    this._onReconnect = callback;
  }

  // Get current open positions from internal tracking
  getOpenPositions() {
    return Array.from(this._openPositions.values());
  }

  // Get open positions count
  getOpenPositionsCount() {
    return this._openPositions.size;
  }

  // Make API request with rate limiting and retry logic
  async apiRequest(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
      ...options.headers,
    };

    // Acquire rate limit tokens before making request
    await this.rateLimiter.acquire(1);
    
    const makeRequest = async () => {
      const response = await fetch(url, { ...options, headers });
      
      // Handle rate limiting (429 Too Many Requests)
      if (response.status === 429) {
        const error = await response.json().catch(() => ({ message: 'Rate limit exceeded' }));
        throw new Error(`TooManyRequestsError: ${error.message || 'Rate limit exceeded'}`);
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Request failed' }));
        throw new Error(error.message || `HTTP ${response.status}`);
      }
      
      return await response.json();
    };
    
    // Use retry handler
    return await this.retryHandler.execute(makeRequest, `API ${endpoint}`);
  }

  // Initialize connection
  async initialize(login, server, password) {
    try {
      this.token = getToken();
      if (!this.token) {
        throw new Error('MetaApi token not configured');
      }

      // Get all accounts
      const accountsResponse = await this.apiRequest('/metatrader_accounts');
      const accounts = accountsResponse.account;
      
      // Find or create account
      let matched = accounts.find((acc) => acc.login === login && acc.server === server);

      if (!matched) {
        throw new Error('Account not found. Please create account in MetaApi dashboard first.');
      }

      this.accountId = matched._id;
      
      // Deploy account
      if (matched.state === 'UNDEPLOYED') {
        await this.apiRequest(`/metatrader_accounts/${this.accountId}/deploy`, {
          method: 'PUT',
        });
        
        // Wait for deployment
        let deployed = false;
        for (let i = 0; i < 60; i++) {
          await new Promise(r => setTimeout(r, 2000));
          const account = await this.apiRequest(`/metatrader_accounts/${this.accountId}`);
          if (account.state === 'DEPLOYED') {
            deployed = true;
            break;
          }
        }
        if (!deployed) {
          throw new Error('Account deployment timeout');
        }
      }

      // Connect to account
      await this.apiRequest(`/metatrader_accounts/${this.accountId}/connect`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      });

      this.isConnected = true;
      console.log('MetaApi connected successfully');
      
      // Initial sync of positions
      await this.syncPositions();
      
      // Start continuous sync
      this.startPositionSync(5000); // Sync every 5 seconds
      
      // Get account info
      const accountInfo = await this.apiRequest(`/metatrader_accounts/${this.accountId}/accountInformation`);
      
      return { success: true, accountInfo };
    } catch (error) {
      console.error('MetaApi initialization error:', error);
      throw error;
    }
  }

  // Start continuous position synchronization
  startPositionSync(intervalMs = 5000) {
    if (this._syncInterval) {
      clearInterval(this._syncInterval);
    }
    
    this._syncInterval = setInterval(async () => {
      if (this.isConnected) {
        try {
          await this.syncPositions();
        } catch (err) {
          console.warn('Position sync error:', err.message);
        }
      }
    }, intervalMs);
  }

  // Stop position synchronization
  stopPositionSync() {
    if (this._syncInterval) {
      clearInterval(this._syncInterval);
      this._syncInterval = null;
    }
  }

  // Sync internal tracking with broker positions
  async syncPositions() {
    if (!this.accountId) return;
    
    try {
      const brokerPositions = await this.getPositions();
      const brokerPositionIds = new Set(brokerPositions.map(p => p.id));
      
      // Check for closed positions (positions that were in our tracking but not in broker)
      const previouslyTracked = new Set(this._openPositions.keys());
      for (const positionId of previouslyTracked) {
        if (!brokerPositionIds.has(positionId)) {
          // Position was closed
          const closedPosition = this._openPositions.get(positionId);
          if (closedPosition) {
            this.tradeLogger.log({
              type: 'POSITION_CLOSED',
              tradeId: positionId,
              symbol: closedPosition.symbol,
              side: closedPosition.side,
              volume: closedPosition.volume,
              reason: closedPosition._closeReason || 'BROKER_SYNC',
              closePrice: closedPosition._closePrice || closedPosition.price,
              profit: closedPosition.profit,
              pnl: closedPosition.pnl
            });
            
            // Notify callbacks
            if (this._onPositionClose) {
              this._onPositionClose(closedPosition);
            }
            
            this._openPositions.delete(positionId);
            this._positionOrder = this._positionOrder.filter(id => id !== positionId);
          }
        }
      }
      
      // Add/update positions from broker
      for (const position of brokerPositions) {
        const existingPosition = this._openPositions.get(position.id);
        
        if (!existingPosition) {
          // New position - this shouldn't happen via sync (trades come from executeTrade)
          // But handle it for reconnection scenarios
          this._openPositions.set(position.id, {
            ...position,
            _openedAt: new Date().toISOString(),
            _brokerPosition: true // Mark as from broker (for sync scenarios)
          });
          this._positionOrder.push(position.id);
          
          this.tradeLogger.log({
            type: 'POSITION_SYNCED',
            tradeId: position.id,
            symbol: position.symbol,
            side: position.type,
            volume: position.volume,
            price: position.price
          });
        } else {
          // Update existing position with latest data
          const updatedPosition = {
            ...existingPosition,
            ...position,
            _openedAt: existingPosition._openedAt // Keep original open time
          };
          this._openPositions.set(position.id, updatedPosition);
          
          // Check for SL/TP triggers
          if (position.stopLoss && position._slTriggered) {
            this.tradeLogger.log({
              type: 'SL_HIT',
              tradeId: position.id,
              symbol: position.symbol,
              slLevel: position.stopLoss,
              currentPrice: position.price
            });
          }
          
          if (position.takeProfit && position._tpTriggered) {
            this.tradeLogger.log({
              type: 'TP_HIT',
              tradeId: position.id,
              symbol: position.symbol,
              tpLevel: position.takeProfit,
              currentPrice: position.price
            });
          }
        }
        
        // Notify update callback
        if (this._onPositionUpdate) {
          this._onPositionUpdate(position);
        }
      }
      
      this._lastSyncTime = new Date();
      
    } catch (error) {
      console.error('Sync positions error:', error);
      throw error;
    }
  }

  // Get symbol price
  async getSymbolPrice(symbol) {
    if (!this.accountId) throw new Error('Not connected');
    
    // Try possible variants
    const variants = this.getSymbolVariants(symbol);
    let lastError = null;
    
    for (const variant of variants) {
      try {
        const price = await this.apiRequest(
          `/metatrader_accounts/${this.accountId}/symbols/${encodeURIComponent(variant)}/price`
        );
        if (price && price.bid != null && price.ask != null) {
          foundRealSymbols[symbol] = variant;
          return price;
        }
      } catch (e) {
        lastError = e;
      }
    }
    
    throw lastError || new Error(`Could not find price for ${symbol}`);
  }

  // Get symbol variants
  getSymbolVariants(symbol) {
    const variants = {
      BTCUSD: ['BTCUSD', 'BTCUSD.m', 'BTCUSD.raw', 'BTCUSD.ecn'],
      EURUSD: ['EURUSD', 'EURUSD.m', 'EURUSD.raw', 'EURUSD.ecn'],
      GBPUSD: ['GBPUSD', 'GBPUSD.m', 'GBPUSD.raw', 'GBPUSD.ecn'],
      USDJPY: ['USDJPY', 'USDJPY.m', 'USDJPY.raw', 'USDJPY.ecn'],
      XAUUSD: ['XAUUSD', 'XAUUSD.m', 'XAUUSD.raw', 'XAUUSD.ecn', 'GOLD'],
    };
    return variants[symbol] || [symbol];
  }

  // Get positions
  async getPositions() {
    if (!this.accountId) throw new Error('Not connected');
    const result = await this.apiRequest(`/metatrader_accounts/${this.accountId}/positions`);
    return result.positions || [];
  }

  // Get positions by magic number
  async getPositionsByMagic(magicNumber) {
    const positions = await this.getPositions();
    return positions.filter(p => p.magic === magicNumber);
  }

  // Calculate SL/TP levels based on USD values
  calculateSLTP(symbol, side, entryPrice, slUSD, tpUSD) {
    const isGold = symbol === 'XAUUSD';
    const isJpy = symbol.includes('JPY');
    
    // Get pip/point value based on symbol
    let pipValue = 0.00001; // Standard 5-digit
    if (isGold) pipValue = 0.01; // Gold uses 2 decimal
    if (isJpy) pipValue = 0.001; // JPY pairs use 3 decimal
    
    // Calculate SL and TP in pips
    const slPips = parseFloat(slUSD) / pipValue;
    const tpPips = parseFloat(tpUSD) / pipValue;
    
    let stopLoss, takeProfit;
    
    if (side === 'BUY') {
      stopLoss = entryPrice - (slPips * pipValue);
      takeProfit = entryPrice + (tpPips * pipValue);
    } else { // SELL
      stopLoss = entryPrice + (slPips * pipValue);
      takeProfit = entryPrice - (tpPips * pipValue);
    }
    
    return { stopLoss, takeProfit };
  }

  // Create buy order with SL/TP
  async createBuyOrder(symbol, volume, options = {}) {
    if (!this.accountId) throw new Error('Not connected');
    
    const realSym = foundRealSymbols[symbol] || symbol;
    
    // Get current price for SL/TP calculation
    let priceData = null;
    if (options.useSLTP) {
      try {
        priceData = await this.getSymbolPrice(symbol);
      } catch (e) {
        console.warn('Could not get price for SL/TP:', e.message);
      }
    }
    
    // Build order request
    const orderRequest = {
      symbol: realSym,
      volume: volume,
      type: 'MARKET',
      side: 'BUY',
      magic: options.magic || 202402,
      comment: options.comment || 'TidalWave',
    };
    
    // Add SL/TP if enabled and price available
    if (options.useSLTP && priceData && options.slUSD && options.tpUSD) {
      const entryPrice = priceData.ask; // Use ask for buy orders
      const { stopLoss, takeProfit } = this.calculateSLTP(
        symbol, 'BUY', entryPrice, options.slUSD, options.tpUSD
      );
      
      orderRequest.stopLoss = stopLoss;
      orderRequest.takeProfit = takeProfit;
    }
    
    const order = await this.apiRequest(
      `/metatrader_accounts/${this.accountId}/orders`,
      {
        method: 'POST',
        body: JSON.stringify(orderRequest),
      }
    );
    
    // Immediately register trade in internal tracking
    const positionData = {
      id: order.orderId || order.positionId || `pending_${Date.now()}`,
      symbol: symbol,
      type: 'POSITION_TYPE_BUY',
      side: 'BUY',
      volume: volume,
      price: priceData?.ask || 0,
      stopLoss: orderRequest.stopLoss,
      takeProfit: orderRequest.takeProfit,
      magic: orderRequest.magic,
      _openedAt: new Date().toISOString(),
      _pending: true, // Mark as pending until confirmed
      _slUSD: options.slUSD,
      _tpUSD: options.tpUSD
    };
    
    // Register immediately (Fix 1: Immediate trade registration)
    this._openPositions.set(positionData.id, positionData);
    this._positionOrder.push(positionData.id);
    
    this.tradeLogger.log({
      type: 'TRADE_OPENED',
      tradeId: positionData.id,
      symbol: symbol,
      side: 'BUY',
      volume: volume,
      price: priceData?.ask || 'MARKET',
      stopLoss: orderRequest.stopLoss,
      takeProfit: orderRequest.takeProfit,
      slUSD: options.slUSD,
      tpUSD: options.tpUSD
    });
    
    // Notify callbacks
    if (this._onPositionUpdate) {
      this._onPositionUpdate(positionData);
    }
    
    // Confirm position after a short delay (broker takes time to create position)
    setTimeout(async () => {
      try {
        const positions = await this.getPositionsByMagic(orderRequest.magic);
        const latestPosition = positions.find(p => 
          p.symbol === realSym && 
          p.type === 'POSITION_TYPE_BUY' &&
          Math.abs(p.volume - volume) < 0.01
        );
        
        if (latestPosition) {
          // Update with confirmed position data
          const confirmedData = {
            ...positionData,
            ...latestPosition,
            id: latestPosition.id,
            _pending: false,
            _confirmedAt: new Date().toISOString()
          };
          
          // Remove pending, add confirmed
          this._openPositions.delete(positionData.id);
          this._openPositions.set(latestPosition.id, confirmedData);
          
          // Update order tracking
          const orderIdx = this._positionOrder.indexOf(positionData.id);
          if (orderIdx > -1) {
            this._positionOrder[orderIdx] = latestPosition.id;
          }
          
          this.tradeLogger.log({
            type: 'TRADE_CONFIRMED',
            tradeId: latestPosition.id,
            symbol: symbol,
            confirmedPrice: latestPosition.price
          });
          
          if (this._onPositionUpdate) {
            this._onPositionUpdate(confirmedData);
          }
        }
      } catch (e) {
        console.warn('Could not confirm position:', e.message);
      }
    }, 2000);
    
    return order;
  }

  // Create sell order with SL/TP
  async createSellOrder(symbol, volume, options = {}) {
    if (!this.accountId) throw new Error('Not connected');
    
    const realSym = foundRealSymbols[symbol] || symbol;
    
    // Get current price for SL/TP calculation
    let priceData = null;
    if (options.useSLTP) {
      try {
        priceData = await this.getSymbolPrice(symbol);
      } catch (e) {
        console.warn('Could not get price for SL/TP:', e.message);
      }
    }
    
    // Build order request
    const orderRequest = {
      symbol: realSym,
      volume: volume,
      type: 'MARKET',
      side: 'SELL',
      magic: options.magic || 202402,
      comment: options.comment || 'TidalWave',
    };
    
    // Add SL/TP if enabled and price available
    if (options.useSLTP && priceData && options.slUSD && options.tpUSD) {
      const entryPrice = priceData.bid; // Use bid for sell orders
      const { stopLoss, takeProfit } = this.calculateSLTP(
        symbol, 'SELL', entryPrice, options.slUSD, options.tpUSD
      );
      
      orderRequest.stopLoss = stopLoss;
      orderRequest.takeProfit = takeProfit;
    }
    
    const order = await this.apiRequest(
      `/metatrader_accounts/${this.accountId}/orders`,
      {
        method: 'POST',
        body: JSON.stringify(orderRequest),
      }
    );
    
    // Immediately register trade in internal tracking
    const positionData = {
      id: order.orderId || order.positionId || `pending_${Date.now()}`,
      symbol: symbol,
      type: 'POSITION_TYPE_SELL',
      side: 'SELL',
      volume: volume,
      price: priceData?.bid || 0,
      stopLoss: orderRequest.stopLoss,
      takeProfit: orderRequest.takeProfit,
      magic: orderRequest.magic,
      _openedAt: new Date().toISOString(),
      _pending: true,
      _slUSD: options.slUSD,
      _tpUSD: options.tpUSD
    };
    
    // Register immediately (Fix 1: Immediate trade registration)
    this._openPositions.set(positionData.id, positionData);
    this._positionOrder.push(positionData.id);
    
    this.tradeLogger.log({
      type: 'TRADE_OPENED',
      tradeId: positionData.id,
      symbol: symbol,
      side: 'SELL',
      volume: volume,
      price: priceData?.bid || 'MARKET',
      stopLoss: orderRequest.stopLoss,
      takeProfit: orderRequest.takeProfit,
      slUSD: options.slUSD,
      tpUSD: options.tpUSD
    });
    
    // Notify callbacks
    if (this._onPositionUpdate) {
      this._onPositionUpdate(positionData);
    }
    
    // Confirm position after a short delay
    setTimeout(async () => {
      try {
        const positions = await this.getPositionsByMagic(orderRequest.magic);
        const latestPosition = positions.find(p => 
          p.symbol === realSym && 
          p.type === 'POSITION_TYPE_SELL' &&
          Math.abs(p.volume - volume) < 0.01
        );
        
        if (latestPosition) {
          const confirmedData = {
            ...positionData,
            ...latestPosition,
            id: latestPosition.id,
            _pending: false,
            _confirmedAt: new Date().toISOString()
          };
          
          this._openPositions.delete(positionData.id);
          this._openPositions.set(latestPosition.id, confirmedData);
          
          const orderIdx = this._positionOrder.indexOf(positionData.id);
          if (orderIdx > -1) {
            this._positionOrder[orderIdx] = latestPosition.id;
          }
          
          this.tradeLogger.log({
            type: 'TRADE_CONFIRMED',
            tradeId: latestPosition.id,
            symbol: symbol,
            confirmedPrice: latestPosition.price
          });
          
          if (this._onPositionUpdate) {
            this._onPositionUpdate(confirmedData);
          }
        }
      } catch (e) {
        console.warn('Could not confirm position:', e.message);
      }
    }, 2000);
    
    return order;
  }

  // Close position with proper error handling and retry
  async closePosition(positionId) {
    if (!this.accountId) throw new Error('Not connected');
    
    // First verify position exists
    const position = this._openPositions.get(positionId);
    if (!position) {
      // Try to sync first
      await this.syncPositions();
      const updatedPosition = this._openPositions.get(positionId);
      if (!updatedPosition) {
        this.tradeLogger.log({
          type: 'CLOSE_FAILED',
          tradeId: positionId,
          error: 'Position not found in tracking'
        });
        throw new Error(`Position not found: ${positionId}`);
      }
    }
    
    this.tradeLogger.log({
      type: 'CLOSE_INITIATED',
      tradeId: positionId,
      symbol: position?.symbol || 'UNKNOWN',
      side: position?.side || 'UNKNOWN'
    });
    
    const closeWithRetry = async () => {
      const result = await this.apiRequest(
        `/metatrader_accounts/${this.accountId}/positions/${positionId}/close`,
        { method: 'DELETE' }
      );
      return result;
    };
    
    try {
      const result = await this.retryHandler.execute(closeWithRetry, `Close position ${positionId}`);
      
      // Mark as closing
      const pos = this._openPositions.get(positionId);
      if (pos) {
        pos._closing = true;
      }
      
      return result;
    } catch (error) {
      // Handle position not found error (already closed by broker)
      if (error.message?.includes('Position not found') || error.message?.includes('404')) {
        this.tradeLogger.log({
          type: 'POSITION_ALREADY_CLOSED',
          tradeId: positionId,
          error: error.message
        });
        // Remove from tracking anyway
        this._openPositions.delete(positionId);
        this._positionOrder = this._positionOrder.filter(id => id !== positionId);
        return { alreadyClosed: true };
      }
      
      console.error('Close position error:', error);
      this.tradeLogger.log({
        type: 'CLOSE_FAILED',
        tradeId: positionId,
        error: error.message
      });
      throw error;
    }
  }

  // Close ALL open positions with proper error handling and rate limiting
  async closeAllPositions() {
    if (!this.accountId) throw new Error('Not connected');
    
    // Get current positions from broker first
    let brokerPositions;
    try {
      brokerPositions = await this.retryHandler.execute(
        () => this.getPositions(),
        'Get positions before close all'
      );
    } catch (err) {
      console.error('Failed to fetch positions before close all:', err);
      brokerPositions = [];
    }
    
    const trackedPositions = Array.from(this._openPositions.values());
    
    // Use broker positions if no tracked positions
    const positions = brokerPositions.length > 0 ? brokerPositions : trackedPositions;
    
    if (positions.length === 0) {
      console.log('No positions to close');
      return { closed: 0, failed: 0, errors: [] };
    }
    
    console.log(`Starting graceful shutdown: closing ${positions.length} positions`);
    
    this.tradeLogger.log({
      type: 'GRACEFUL_SHUTDOWN_START',
      positionCount: positions.length
    });
    
    // Determine closing order based on configuration
    let positionsToClose = [...positions];
    if (this.closingMethod === 'FIFO') {
      // First In First Out - close in the order they were opened
      positionsToClose.sort((a, b) => {
        const aIndex = this._positionOrder.indexOf(a.id);
        const bIndex = this._positionOrder.indexOf(b.id);
        return aIndex - bIndex;
      });
    } else if (this.closingMethod === 'LIFO') {
      // Last In First Out - close newest first
      positionsToClose.sort((a, b) => {
        const aIndex = this._positionOrder.indexOf(a.id);
        const bIndex = this._positionOrder.indexOf(b.id);
        return bIndex - aIndex;
      });
    }
    
    const results = { closed: 0, failed: 0, errors: [] };
    
    for (let i = 0; i < positionsToClose.length; i++) {
      const position = positionsToClose[i];
      
      // Skip if already closed
      if (brokerPositions.length > 0 && !brokerPositions.find(p => p.id === position.id)) {
        console.log(`Position ${position.id} already closed by broker`);
        this._openPositions.delete(position.id);
        this._positionOrder = this._positionOrder.filter(id => id !== position.id);
        continue;
      }
      
      try {
        // Mark with close reason
        const posInTracking = this._openPositions.get(position.id);
        if (posInTracking) {
          posInTracking._closeReason = 'GRACEFUL_SHUTDOWN';
        }
        
        await this.closePosition(position.id);
        results.closed++;
        
        // Rate limit: wait between closes
        if (i < positionsToClose.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
      } catch (error) {
        results.failed++;
        results.errors.push({ positionId: position.id, error: error.message });
        console.error(`Failed to close position ${position.id}:`, error.message);
        
        // If position not found, remove from tracking
        if (error.message?.includes('Position not found')) {
          this._openPositions.delete(position.id);
          this._positionOrder = this._positionOrder.filter(id => id !== position.id);
        }
      }
    }
    
    this.tradeLogger.log({
      type: 'GRACEFUL_SHUTDOWN_COMPLETE',
      closed: results.closed,
      failed: results.failed,
      method: this.closingMethod
    });
    
    // Wait for broker to process closes, then sync
    setTimeout(async () => {
      await this.syncPositions();
    }, 3000);
    
    return results;
  }

  // Force close a specific position (Fix 7: Force-close function)
  async forceClosePosition(positionId, maxRetries = 3) {
    if (!this.accountId) throw new Error('Not connected');
    
    const position = this._openPositions.get(positionId);
    if (!position) {
      // Try to find by syncing first
      await this.syncPositions();
      const updatedPosition = this._openPositions.get(positionId);
      if (!updatedPosition) {
        throw new Error('Position not found after sync');
      }
    }
    
    this.tradeLogger.log({
      type: 'FORCE_CLOSE_INITIATED',
      tradeId: positionId,
      maxRetries
    });
    
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Force close attempt ${attempt}/${maxRetries} for position ${positionId}`);
        
        // Try normal close first
        await this.closePosition(positionId);
        
        // Wait and verify
        await new Promise(resolve => setTimeout(resolve, 1000));
        await this.syncPositions();
        
        // Check if closed
        if (!this._openPositions.has(positionId)) {
          this.tradeLogger.log({
            type: 'FORCE_CLOSE_SUCCESS',
            tradeId: positionId,
            attempt
          });
          return { success: true, attempt };
        }
        
      } catch (error) {
        lastError = error;
        console.warn(`Force close attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
    
    // All attempts failed
    this.tradeLogger.log({
      type: 'FORCE_CLOSE_FAILED',
      tradeId: positionId,
      error: lastError?.message || 'Unknown error',
      attempts: maxRetries
    });
    
    if (this._onTradeError) {
      this._onTradeError({
        type: 'FORCE_CLOSE_FAILED',
        positionId,
        error: lastError?.message
      });
    }
    
    throw new Error(`Force close failed after ${maxRetries} attempts: ${lastError?.message}`);
  }

  // Set closing method
  setClosingMethod(method) {
    if (['FIFO', 'LIFO'].includes(method)) {
      this.closingMethod = method;
    }
  }

  // Get trade logger events
  getTradeLog() {
    return this.tradeLogger.getEvents();
  }

  // Clear trade log
  clearTradeLog() {
    this.tradeLogger.clear();
  }

  // Get account info
  async getAccountInfo() {
    if (!this.accountId) throw new Error('Not connected');
    return await this.apiRequest(`/metatrader_accounts/${this.accountId}/accountInformation`);
  }

  // Disconnect with graceful shutdown (Fix 2: Graceful shutdown on disconnect)
  async disconnect(closeTradesOnDisconnect = true) {
    // Stop position sync first
    this.stopPositionSync();
    
    if (closeTradesOnDisconnect && this._openPositions.size > 0) {
      console.log('Closing all positions before disconnect...');
      await this.closeAllPositions();
    }
    
    if (this.accountId) {
      try {
        await this.apiRequest(`/metatrader_accounts/${this.accountId}/disconnect`, {
          method: 'PUT',
        });
      } catch (e) {
        console.log('Error disconnecting:', e);
      }
    }
    
    this.isConnected = false;
    this.accountId = null;
    
    // Clear internal tracking
    this._openPositions.clear();
    this._positionOrder = [];
    
    this.tradeLogger.log({
      type: 'DISCONNECTED',
      tradesClosed: closeTradesOnDisconnect
    });
  }

  // Handle reconnection (Fix 5: Reconnection handling)
  async reconnect() {
    if (this.isConnecting) {
      console.log('Already reconnecting...');
      return;
    }
    
    this.isConnecting = true;
    
    this.tradeLogger.log({
      type: 'RECONNECT_START'
    });
    
    try {
      // Sync positions after reconnection
      await this.syncPositions();
      
      // Restart position sync
      this.startPositionSync(5000);
      
      this.isConnecting = false;
      this.isConnected = true;
      
      this.tradeLogger.log({
        type: 'RECONNECT_SUCCESS',
        openPositions: this._openPositions.size
      });
      
      if (this._onReconnect) {
        this._onReconnect({ success: true, positions: this.getOpenPositions() });
      }
      
    } catch (error) {
      this.isConnecting = false;
      
      this.tradeLogger.log({
        type: 'RECONNECT_FAILED',
        error: error.message
      });
      
      if (this._onReconnect) {
        this._onReconnect({ success: false, error: error.message });
      }
      
      throw error;
    }
  }

  // Check if connected
  isReady() {
    return this.isConnected && this.accountId !== null;
  }
}

// Export singleton instance
export const metaApiService = new MetaApiService();
export default metaApiService;
